/**
 * PhotoPulsePicker — community-photo picker + submit modal.
 *
 * Two tabs:
 *   1. Browse   — search the approved PhotoPulse library (empty state
 *                 shows a friendly "coming soon" note while the
 *                 backend flag is off)
 *   2. Submit   — contribute your own image URL to the library. Queued
 *                 for moderation; contributors are told the review flow.
 *
 * Result: caller receives a photo object shaped like the Unsplash one,
 * so the editor's insert logic can stay identical.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Search, X, Loader2, Camera, Upload, CheckCircle2 } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

export default function PhotoPulsePicker({ open, onClose, onPick, initialQuery = "" }) {
  const [tab, setTab] = useState("browse");        // browse | submit
  const [status, setStatus] = useState(null);       // {active, photo_count}
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery);
    setNote(null);
    setError(null);
    (async () => {
      try {
        const r = await fetch(`${BASE}/photopulse/status`);
        const d = await r.json();
        setStatus(d);
        // If offline, jump straight to Submit — that's the only useful action
        if (d && d.active === false) setTab("browse");   // still show browse w/ coming-soon
      } catch { /* noop */ }
    })();
    setTimeout(() => inputRef.current?.focus(), 60);
  }, [open, initialQuery]);

  const runSearch = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${BASE}/photopulse/search`, window.location.origin);
      url.searchParams.set("q", (q || "").trim());
      url.searchParams.set("per_page", "18");
      const r = await fetch(url.toString().replace(window.location.origin, ""));
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setResults(d.results || []);
      setNote(d.note || null);
    } catch (e) {
      setError(e.message || "Search failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && tab === "browse") runSearch(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const commit = useCallback((photo) => {
    // Normalize into the same shape as the Unsplash picker so the caller
    // treats both sources identically.
    onPick?.({
      photo_id:          photo.photo_id,
      image_url:         photo.image_url,
      thumb_url:         photo.thumb_url,
      alt_description:   photo.alt_description || photo.title || "",
      photographer:      photo.photographer || {},
      unsplash_link:     "",   // n/a for PhotoPulse
      download_location: "",   // n/a — no tracking beacon required
      width:             photo.width || 0,
      height:            photo.height || 0,
      source:            "photopulse",
      license:           photo.license,
    });
    onClose?.();
  }, [onPick, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-slate-900/70 backdrop-blur-sm p-4 overflow-y-auto"
      data-testid="photopulse-picker-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-5xl mt-8 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-testid="photopulse-picker-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
              <Camera className="w-3.5 h-3.5" /> Community library
            </div>
            <div className="text-lg font-bold text-slate-900">
              PhotoPulse
              {status && (
                <span
                  className={`ml-2 text-[10px] font-semibold uppercase tracking-widest align-middle px-2 py-0.5 rounded-full ${
                    status.active ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                  }`}
                  data-testid="photopulse-status-badge"
                >
                  {status.active ? "Live" : "Warming up"}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-slate-100"
            data-testid="photopulse-picker-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-6 border-b border-slate-100 text-sm">
          <TabBtn active={tab === "browse"} onClick={() => setTab("browse")} testId="photopulse-tab-browse">Browse</TabBtn>
          <TabBtn active={tab === "submit"} onClick={() => setTab("submit")} testId="photopulse-tab-submit">Submit your photo</TabBtn>
        </div>

        {tab === "browse" ? (
          <BrowsePane
            query={query}
            setQuery={setQuery}
            inputRef={inputRef}
            onSearch={runSearch}
            results={results}
            loading={loading}
            error={error}
            note={note}
            onCommit={commit}
            onSwitchToSubmit={() => setTab("submit")}
          />
        ) : (
          <SubmitPane onSubmitted={() => setTab("browse")} />
        )}

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
          PhotoPulse is our community-owned photo library. Photographers keep credit; approved contributors earn Social Capital.
        </div>
      </div>
    </div>
  );
}


function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-3 border-b-2 -mb-px font-semibold transition-colors ${
        active
          ? "border-slate-900 text-slate-900"
          : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}


function BrowsePane({ query, setQuery, inputRef, onSearch, results, loading, error, note, onCommit, onSwitchToSubmit }) {
  return (
    <>
      <form
        onSubmit={(e) => { e.preventDefault(); onSearch(query); }}
        className="px-6 py-4 border-b border-slate-100"
      >
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search PhotoPulse — 'kerala backwaters', 'farmer at dawn'…"
            className="w-full pl-10 pr-24 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            data-testid="photopulse-picker-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-slate-900 text-white text-xs font-semibold rounded-lg hover:bg-slate-700 disabled:opacity-40"
            data-testid="photopulse-picker-search"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </button>
        </div>
        {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
      </form>

      <div className="p-6 min-h-[280px] max-h-[54vh] overflow-y-auto">
        {loading && results.length === 0 && (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Searching PhotoPulse…
          </div>
        )}
        {!loading && results.length === 0 && (
          <div className="text-center py-12 max-w-md mx-auto">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 grid place-items-center mx-auto mb-4">
              <Camera className="w-7 h-7 text-amber-600" />
            </div>
            <h3 className="font-bold text-slate-900 text-lg mb-1">
              {note ? "PhotoPulse is warming up" : "No matches yet"}
            </h3>
            <p className="text-sm text-slate-500 leading-relaxed">
              {note || "Nothing in the community library matches this search yet. Be the first to add one — your photo will help other writers tell better stories."}
            </p>
            <button
              type="button"
              onClick={onSwitchToSubmit}
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700"
              data-testid="photopulse-empty-submit"
            >
              <Upload className="w-4 h-4" /> Submit a photo
            </button>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {results.map((p) => (
            <button
              key={p.photo_id}
              type="button"
              onClick={() => onCommit(p)}
              className="group relative rounded-lg overflow-hidden bg-slate-100 aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid={`photopulse-photo-${p.photo_id}`}
            >
              <img
                src={p.thumb_url || p.image_url}
                alt={p.alt_description || ""}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="text-[10px] text-white/90 truncate">
                  {p.photographer?.name || "PhotoPulse contributor"}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}


function SubmitPane({ onSubmitted }) {
  const [form, setForm] = useState({
    title: "", description: "", image_url: "", thumb_url: "", place: "", topic: "",
    photographer_name: "", photographer_profile_url: "", license: "CC-BY-4.0",
  });
  const [state, setState] = useState("idle"); // idle | saving | done | error
  const [msg, setMsg] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("saving");
    setMsg(null);
    try {
      const body = {
        title:       form.title,
        description: form.description || null,
        image_url:   form.image_url,
        thumb_url:   form.thumb_url || null,
        place:       form.place || null,
        topic:       form.topic || null,
        license:     form.license,
        photographer: form.photographer_name
          ? { name: form.photographer_name, profile_url: form.photographer_profile_url || null }
          : null,
      };
      const r = await fetch(`${BASE}/photopulse/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `HTTP ${r.status}`);
      }
      const d = await r.json();
      setState("done");
      setMsg(d.note || "Submitted for review — thank you!");
    } catch (err) {
      setState("error");
      setMsg(err.message || "Submission failed");
    }
  };

  if (state === "done") {
    return (
      <div className="p-8 text-center max-w-md mx-auto">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
        <h3 className="font-bold text-lg text-slate-900 mb-1">Thanks — your photo is queued</h3>
        <p className="text-sm text-slate-500 leading-relaxed" data-testid="photopulse-submit-success">{msg}</p>
        <button
          type="button"
          onClick={onSubmitted}
          className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200"
        >
          Back to browse
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="p-6 space-y-3 max-h-[54vh] overflow-y-auto">
      <FormRow label="Title *">
        <input required minLength={3} value={form.title} onChange={set("title")}
          className="input" data-testid="photopulse-submit-title"
          placeholder="e.g. Sunrise at Kumarakom" />
      </FormRow>
      <FormRow label="Image URL *">
        <input required type="url" value={form.image_url} onChange={set("image_url")}
          className="input" data-testid="photopulse-submit-image-url"
          placeholder="https://..." />
      </FormRow>
      <div className="grid grid-cols-2 gap-3">
        <FormRow label="Place">
          <input value={form.place} onChange={set("place")} className="input"
            data-testid="photopulse-submit-place"
            placeholder="e.g. Kerala, India" />
        </FormRow>
        <FormRow label="Topic">
          <input value={form.topic} onChange={set("topic")} className="input"
            data-testid="photopulse-submit-topic"
            placeholder="e.g. nature, farmers" />
        </FormRow>
      </div>
      <FormRow label="Short description">
        <textarea rows={2} value={form.description} onChange={set("description")}
          className="input" data-testid="photopulse-submit-description"
          placeholder="One line about the photo (optional)." />
      </FormRow>
      <div className="grid grid-cols-2 gap-3">
        <FormRow label="Photographer name">
          <input value={form.photographer_name} onChange={set("photographer_name")}
            className="input" data-testid="photopulse-submit-photographer" />
        </FormRow>
        <FormRow label="Photographer profile URL">
          <input type="url" value={form.photographer_profile_url}
            onChange={set("photographer_profile_url")}
            className="input" placeholder="https://" />
        </FormRow>
      </div>
      <FormRow label="License">
        <select value={form.license} onChange={set("license")} className="input"
          data-testid="photopulse-submit-license">
          <option value="CC-BY-4.0">CC BY 4.0 — credit required</option>
          <option value="CC-BY-SA-4.0">CC BY-SA 4.0 — credit + share-alike</option>
          <option value="CC0-1.0">CC0 — public domain</option>
          <option value="all-rights-reserved">All rights reserved (own it, allow use)</option>
        </select>
      </FormRow>

      {msg && state === "error" && (
        <div className="text-xs text-red-600" data-testid="photopulse-submit-error">{msg}</div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="submit"
          disabled={state === "saving"}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 disabled:opacity-40"
          data-testid="photopulse-submit-btn"
        >
          {state === "saving" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Submit for review
        </button>
      </div>
      <style>{`.input{width:100%;padding:0.55rem 0.75rem;border:1px solid rgb(203 213 225);border-radius:0.5rem;font-size:0.875rem;background:#fff}
      .input:focus{outline:2px solid rgb(59 130 246);outline-offset:1px;border-color:transparent}`}</style>
    </form>
  );
}


function FormRow({ label, children }) {
  return (
    <label className="block text-sm">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
